import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from './button';
import { Text } from './text';

export interface ConfirmOptions {
  title: string;
  text?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

let showConfirm: ((pending: PendingConfirm) => void) | null = null;

export const confirmDialog = (options: ConfirmOptions): Promise<boolean> =>
  new Promise((resolve) => {
    if (!showConfirm) {
      resolve(false);
      return;
    }
    showConfirm({ ...options, resolve });
  });

export const ConfirmDialogHost = (): JSX.Element => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  useEffect(() => {
    showConfirm = setPending;
    return () => {
      showConfirm = null;
    };
  }, []);

  const close = (confirmed: boolean): void => {
    pending?.resolve(confirmed);
    setPending(null);
  };

  return (
    <Dialog
      open={pending != null}
      onClose={() => close(false)}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
    >
      <DialogTitle id="confirm-dialog-title" sx={{ pb: 1 }}>
        {pending?.title}
      </DialogTitle>
      {pending?.text ? (
        <DialogContent>
          <Text tone="muted">{pending.text}</Text>
        </DialogContent>
      ) : null}
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button variant="ghost" onClick={() => close(false)}>
          {pending?.cancelLabel ?? t('common.cancel')}
        </Button>
        <Button
          variant={pending?.danger ? 'danger' : 'primary'}
          onClick={() => close(true)}
          autoFocus
        >
          {pending?.confirmLabel ?? t('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
